/**
 * @file test.ts
 * @description 测试页面路由（sdk-test 及各测试页），统一在此维护。
 *
 * 打包控制：条件编译变量 `__INCLUDE_TEST__`（'true'/'false'）——
 * - 本地开发（serve）恒为 'true'；
 * - 生产构建：仅测试构建类型（VITE_BUILD_FLAVOR === 'test'）为 'true'。
 * 业务路由（router/index.ts）无需感知条件编译，统一通过 getTestRoutes() 获取；
 * 非 SDK 测试页（键盘测试/Element 组件测试等）也集中在此，避免散落在业务路由中。
 *
 * 调试页导航：DebugPanel 通过路由 meta.debug 自动收集入口——新增调试路由只需在此
 * 添加并带上 meta（debug/debugLabel/debugIcon），面板即自动出现，无需再改面板。
 */

import type { RouteRecordRaw } from 'vue-router'

// 调试页路由元信息类型扩展（DebugPanel 据此自动生成导航列表）
declare module 'vue-router' {
  interface RouteMeta {
    /** 标记为调试页：DebugPanel 导航自动收集。 */
    debug?: boolean
    /** 调试页显示名称。 */
    debugLabel?: string
    /** 调试页图标（emoji）。 */
    debugIcon?: string
  }
}

/**
 * 测试路由集合。
 */
export interface TestRouteBundle {
  /** 挂载在 "/" 布局 children 下的测试路由（路径相对 "/"，如 sdk-test/base → /sdk-test/base）。 */
  childRoutes: RouteRecordRaw[]
  /** 顶层测试路由（独立页面，不挂在 "/" 布局下）。 */
  topRoutes: RouteRecordRaw[]
}

// #if (__INCLUDE_TEST__ === 'true')
export const TEST_CHILD_ROUTES: RouteRecordRaw[] = [
  {
    path: 'element-test',
    name: 'element-test',
    component: () => import('@/views/ElementTestView.vue'),
    meta: { debug: true, debugLabel: 'Element 组件测试', debugIcon: '🧩' },
  },
  {
    path: 'sdk-test/base',
    name: 'sdk-test-base',
    component: () => import('@/views/sdk-test/base/index.vue'),
    meta: { debug: true, debugLabel: 'SDK 基础测试', debugIcon: '🧰' },
  },
  {
    path: 'sdk-test/gcome-ap-keyboard',
    name: 'sdk-test-gcome-ap-keyboard',
    component: () => import('@/views/sdk-test/gcome-ap-keyboard/index.vue'),
    meta: { debug: true, debugLabel: 'GCOME_AP 磁轴键盘', debugIcon: '⌨️' },
  },
  {
    path: 'sdk-test/vt700',
    name: 'sdk-test-vt700',
    component: () => import('@/views/sdk-test/vt700/index.vue'),
    meta: { debug: true, debugLabel: 'VT700 测试', debugIcon: '🖱️' },
  },
  {
    path: 'sdk-test/vt2-ultra-v2',
    name: 'sdk-test-vt2-ultra-v2',
    component: () => import('@/views/sdk-test/vt2-ultra-v2/index.vue'),
    meta: { debug: true, debugLabel: 'VT2 ULTRA V2 OTA', debugIcon: '📡' },
  },
  {
    path: 'sdk-test/rt-mouse',
    name: 'sdk-test-rt-mouse',
    component: () => import('@/views/sdk-test/rt-mouse/index.vue'),
    meta: { debug: true, debugLabel: 'RT 磁轴鼠标', debugIcon: '🖱️' },
  },
  {
    path: 'sdk-test/mouse-overlay',
    name: 'sdk-test-mouse-overlay',
    component: () => import('@/views/sdk-test/mouse-overlay/index.vue'),
    meta: { debug: true, debugLabel: '鼠标叠图检查', debugIcon: '🖼️' },
  },
  {
    path: 'sdk-test/ch315-keyboard',
    name: 'sdk-test-ch315-keyboard',
    component: () => import('@/views/sdk-test/ch315-keyboard/index.vue'),
    meta: { debug: true, debugLabel: 'CH315 磁轴键盘', debugIcon: '⌨️' },
  },
]

export const TEST_TOP_ROUTES: RouteRecordRaw[] = [
  {
    path: '/keyboard',
    name: 'keyboard',
    component: () => import('@/views/KeyboardTesting.vue'),
    meta: { debug: true, debugLabel: '键盘测试', debugIcon: '🎹' },
  },
]
// #endif

/**
 * 获取测试路由：生产构建（__INCLUDE_TEST__ 为 'false'）时测试路由常量被条件编译移除，
 * 这里用 typeof 安全探测并回退为空数组，使业务路由无需感知条件编译标记。
 */
export function getTestRoutes(): TestRouteBundle {
  return {
    childRoutes: typeof TEST_CHILD_ROUTES !== 'undefined' ? TEST_CHILD_ROUTES : [],
    topRoutes: typeof TEST_TOP_ROUTES !== 'undefined' ? TEST_TOP_ROUTES : [],
  }
}
